import { Controller } from "@hotwired/stimulus"

const YEAR = 31536000
const MOBILE_QUERY = "(max-width: 768px)"

export default class extends Controller {
  static targets = ["panel", "groups", "group", "label", "menuToggle", "menuToggleLabel", "mobileOpen", "backdrop"]
  static values = {
    collapsedCookie: { type: String, default: "settings_nav_collapsed" },
    groupsCookie: { type: String, default: "settings_nav_collapsed_groups" }
  }

  connect() {
    this.media = window.matchMedia(MOBILE_QUERY)
    this.boundCloseOnEscape = this.closeOnEscape.bind(this)
    document.addEventListener("keydown", this.boundCloseOnEscape)
    this.syncMenuToggle()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundCloseOnEscape)
    document.body.classList.remove("settings-nav-drawer-open")
  }

  toggleMenu() {
    if (this.isMobile()) {
      if (this.panelTarget.classList.contains("is-mobile-open")) {
        this.closeMobile()
      } else {
        this.openMobile()
      }
      return
    }

    const collapsed = !this.panelTarget.classList.contains("is-collapsed")
    this.panelTarget.classList.toggle("is-collapsed", collapsed)
    this.writeCookie(this.collapsedCookieValue, collapsed ? "true" : "")
    this.syncMenuToggle()
  }

  openMobile() {
    this.panelTarget.classList.add("is-mobile-open")
    this.backdropTarget.hidden = false
    document.body.classList.add("settings-nav-drawer-open")
    this.syncMenuToggle()
    this.menuToggleTarget.focus()
  }

  closeMobile() {
    this.panelTarget.classList.remove("is-mobile-open")
    this.backdropTarget.hidden = true
    document.body.classList.remove("settings-nav-drawer-open")
    this.syncMenuToggle()
  }

  toggleGroup(event) {
    if (event.type === "keydown") event.preventDefault()

    const group = event.currentTarget.closest("[data-settings-nav-target='group']")
    if (!group) return

    const destinations = group.querySelector(".settings-nav-destinations")
    const caret = event.currentTarget.querySelector(".ph")
    const collapsing = event.currentTarget.getAttribute("aria-expanded") === "true"

    event.currentTarget.setAttribute("aria-expanded", collapsing ? "false" : "true")
    group.classList.toggle("is-collapsed", collapsing)
    if (destinations) destinations.hidden = collapsing
    if (caret) caret.className = `ph ${collapsing ? "ph-caret-down" : "ph-caret-up"}`

    this.persistCollapsedGroups()
  }

  closeOnEscape(event) {
    if (event.key !== "Escape") return
    if (!this.panelTarget.classList.contains("is-mobile-open")) return
    this.closeMobile()
    if (this.hasMobileOpenTarget) this.mobileOpenTarget.focus()
  }

  isMobile() {
    return this.media.matches
  }

  syncMenuToggle() {
    const mobileOpen = this.panelTarget.classList.contains("is-mobile-open")
    const collapsed = this.panelTarget.classList.contains("is-collapsed")
    const expanded = this.isMobile() ? mobileOpen : !collapsed
    this.menuToggleTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
    if (this.hasMenuToggleLabelTarget) {
      this.menuToggleLabelTarget.textContent = expanded ? "Collapse settings menu" : "Open settings menu"
    }
  }

  persistCollapsedGroups() {
    const keys = this.groupTargets
      .filter((group) => group.classList.contains("is-collapsed"))
      .map((group) => group.dataset.groupKey)
    this.writeCookie(this.groupsCookieValue, keys.join(","))
  }

  writeCookie(name, value) {
    const encoded = encodeURIComponent(value)
    if (value) {
      document.cookie = `${name}=${encoded}; path=/; max-age=${YEAR}; SameSite=Lax`
    } else {
      document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`
    }
  }
}
